/**
 * rbacStore.ts — current user's effective permissions (from the backend RBAC).
 * Roles come from the OIDC JWT; role→permission mapping is managed by admins.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { api } from '@/scripts/api'

export const useRbacStore = defineStore('rbac', () => {
  const permissions = ref<Set<string>>(new Set())
  const roles = ref<string[]>([])
  const isAdmin = ref(false)
  const loaded = ref(false)

  async function fetchMyPermissions() {
    try {
      const res = await api.fetchApi('/me/permissions', { cache: 'no-store' })
      if (!res.ok) return
      const data = (await res.json()) as {
        roles?: string[]
        isAdmin?: boolean
        permissions?: string[]
      }
      roles.value = data.roles ?? []
      isAdmin.value = !!data.isAdmin
      permissions.value = new Set(data.permissions ?? [])
      loaded.value = true
    } catch {
      // leave defaults (no permissions) on failure
    }
  }

  /** True if the user may perform the given "resource:action" operation. */
  function can(key: string): boolean {
    return isAdmin.value || permissions.value.has(key)
  }

  const canManageRbac = computed(() => isAdmin.value)

  return {
    permissions,
    roles,
    isAdmin,
    loaded,
    fetchMyPermissions,
    can,
    canManageRbac
  }
})
